import React, {useContext, useState} from "react";
import {Alert, Button, Form, Input, message, Modal, Radio, Select, Space} from "antd";
import {VulReportServiceContext} from "../../Contexts";
import {InterestFeedbackOption, InterestMemorySuggestion} from "../../domain/InterestMemory";

interface Props {
    reportId: string;
    interests: InterestFeedbackOption[];
    selectedItemIds: string[];
    onSaved: () => void;
    onCancel: () => void;
}

export default function VulReportInterestFeedback({reportId, interests, selectedItemIds, onSaved, onCancel}: Props) {
    const service = useContext(VulReportServiceContext);
    const eligible = interests.filter(interest => selectedItemIds.some(id => interest.itemIds.includes(id)));
    const [interestId, setInterestId] = useState<string | undefined>(() => eligible.length === 1 ? eligible[0].id : undefined);
    const [feedback, setFeedback] = useState("");
    const [text, setText] = useState("");
    const [suggestions, setSuggestions] = useState<InterestMemorySuggestion[]>([]);
    const [selectedSuggestion, setSelectedSuggestion] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const target = eligible.find(interest => interest.id === interestId);
    const targetItemIds = target ? selectedItemIds.filter(id => target.itemIds.includes(id)) : [];

    async function submit(action: "suggest" | "save") {
        if (!target) {
            return;
        }

        setBusy(true);
        setError(undefined);

        try {
            const request = {interestId: target.id, itemIds: targetItemIds, feedback};

            if (action === "suggest") {
                const result = await service.suggestInterestMemory(reportId, request);

                setSuggestions(result);
                setSelectedSuggestion(undefined);
                setText("");
            } else {
                await service.saveInterestMemory(reportId, {...request, text});

                message.success("Rule added to the interest's memory. It will apply the next time items are evaluated.");
                onSaved();
            }
        } catch (error: any) {
            setError(error?.response?.data?.message ?? (action === "suggest"
                ? "Unable to generate suggestions."
                : "Unable to add the rule."));
        } finally {
            setBusy(false);
        }
    }

    return <Modal open title="Not interested" width={800} onCancel={onCancel}
                  style={{top: 24, paddingBottom: 0}}
                  styles={{
                      container: {display: "flex", flexDirection: "column", maxHeight: "calc(100dvh - 48px)"},
                      header: {flexShrink: 0},
                      body: {minHeight: 0, overflowY: "auto", scrollbarGutter: "stable", paddingRight: 16},
                      footer: {flexShrink: 0}
                  }}
                  closable={!busy} keyboard={!busy} mask={{closable: false}}
                  footer={[
                      <Button key="cancel" disabled={busy} onClick={onCancel}>Cancel</Button>,
                      <Button key="save" type="primary" loading={busy} disabled={!target || !selectedSuggestion || !text.trim()}
                              onClick={() => submit("save")}>Add rule</Button>
                  ]}>
        <p>Optionally describe why the selected items are not relevant, then click <strong>Suggest rules</strong> to generate one or more alternative exclusion rules.
            Choose one rule to review and add to the selected interest's memory.</p>
        {error && <Alert type="error" showIcon title={error}/>}
        <Form layout="vertical" disabled={busy} style={{padding: 0, marginBottom: 0}}>
            <Form.Item label="Interest" required>
                <Select value={interestId} placeholder="Select an interest"
                        style={{width: "100%", maxWidth: 400}}
                        options={eligible.map(interest => ({value: interest.id, label: interest.name}))}
                        onChange={id => {
                            setInterestId(id);
                            setSuggestions([]);
                            setSelectedSuggestion(undefined);
                            setText("");
                            setError(undefined);
                        }}/>
            </Form.Item>
            {target && eligible.length > 1 && <p>
                {targetItemIds.length < selectedItemIds.length ? <>
                    Only {targetItemIds.length} of the {selectedItemIds.length} selected items {targetItemIds.length === 1 ? "matches" : "match"} the interest “{target.name}”.
                    {" "}Suggestions will be based on {targetItemIds.length === 1 ? "this item" : `these ${targetItemIds.length} items`}.
                </> : <>
                    {selectedItemIds.length === 1 ? "The selected item matches" : `All ${selectedItemIds.length} selected items match`} the interest “{target.name}” and will be used to suggest rules.
                </>}
            </p>}
            <Form.Item label="Reason for exclusion (optional)"
                       extra="A reason helps AI distinguish whether the product, issue type, or context is irrelevant.">
                <Input.TextArea value={feedback} onChange={event => {
                    setFeedback(event.target.value);
                    setSuggestions([]);
                    setSelectedSuggestion(undefined);
                    setText("");
                }} maxLength={2000} rows={3}/>
            </Form.Item>
            <Form.Item>
                <Button disabled={!target} loading={busy} onClick={() => submit("suggest")}>Suggest rules</Button>
            </Form.Item>
            {suggestions.length > 0 && <Form.Item label="Suggested rules">
                <Radio.Group value={selectedSuggestion} onChange={event => {
                    setSelectedSuggestion(event.target.value);
                    setText(event.target.value);
                }}>
                    <Space orientation="vertical">
                        {suggestions.map(suggestion => <Radio key={suggestion.text} value={suggestion.text}>
                            <strong>{suggestion.text}</strong><br/>{suggestion.impact}
                        </Radio>)}
                    </Space>
                </Radio.Group>
            </Form.Item>}
            {selectedSuggestion && <Form.Item label="Rule to add" required>
                <Input.TextArea value={text} onChange={event => setText(event.target.value)} maxLength={1000} rows={4}/>
            </Form.Item>}
        </Form>
        <p>The added rule extends the interest definition and applies at the next item evaluation.
            Adding a rule does not start an evaluation or change existing results.
            Items may still appear through other interests or manual views.</p>
    </Modal>;
}
